import { EnvironmentInjector, Injectable, inject, runInInjectionContext } from "@angular/core";
import { Firestore } from "@angular/fire/firestore";
import { Database, equalTo, list, object, orderByChild, query, ref } from '@angular/fire/database';
import { Actions, createEffect, ofType } from "@ngrx/effects";

import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { AppActions } from "./app.action-types";
import { map, switchMap } from "rxjs/operators";
import { QueryChange } from "rxfire/database";
import { About } from "./models/about";


@Injectable()
export class AppEffects {
    private injector = inject(EnvironmentInjector);
    private inCtx = <T>(fn: () => T): T => runInInjectionContext(this.injector, fn);

    fetchHours$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchHours),
            switchMap(() => this.inCtx(() => list(ref(this.db, 'hours')))),
            map((resp: QueryChange[]) => {
                const hours = resp
                    .map(qc => ({ [qc.snapshot.key]: { ...qc.snapshot.val(), id: qc.snapshot.key } }))
                    .reduce((acc, cur) => ({ ...acc, ...cur }), {})
                return AppActions.storeHours({ hours })
            })
        )
    )

    fetchAbout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchAbout),
            switchMap(() => this.inCtx(() => list(query(ref(this.db, 'about'), orderByChild('isActive'), equalTo(true))))),
            map((resp: QueryChange[]) => {
                const about = resp
                    .map(qc => {
                        const data = qc.snapshot.val() as About
                        return { ...data.content }
                    })
                    .reduce((acc, cur) => ({ ...acc, ...cur }), {})
                return AppActions.storeAbout({ about })
            })
        )
    )

    fetchProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchProducts),
            switchMap(() => this.inCtx(() => list(ref(this.db, 'services')))),
            map((resp: QueryChange[]) => {
                const products = resp
                    .map(qc => ({ [qc.snapshot.key]: { ...qc.snapshot.val(), id: qc.snapshot.key } }))
                    .reduce((acc, cur) => ({ ...acc, ...cur }), {})
                return AppActions.storeProducts({ products })
            })
        )
    )

    fetchTeamMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchTeamMembers),
            switchMap(() => this.inCtx(() => list(ref(this.db, 'team')))),
            map((resp: QueryChange[]) => {
                const members = resp
                    .map(qc => ({ [qc.snapshot.key]: { ...qc.snapshot.val(), id: qc.snapshot.key } }))
                    .reduce((acc, cur) => ({ ...acc, ...cur }), {})
                return AppActions.storeTeamMembers({ members })
            })
        )
    )

    fetchGalleryImages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchGalleryImages),
            switchMap(() => this.inCtx(() => list(ref(this.db, 'gallery')))),
            map((resp: QueryChange[]) => {
                const gallery = resp
                    .map(qc => ({ [qc.snapshot.key]: { ...qc.snapshot.val(), id: qc.snapshot.key } }))
                    .reduce((acc, cur) => ({ ...acc, ...cur }), {})
                return AppActions.storeGalleryImages({ gallery })
            })
        )
    )

    fetchJoinBannerState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchShowJoinBanner),
            switchMap(() => this.inCtx(() => object(ref(this.db, 'misc/showJoinBanner')))),
            map((resp: QueryChange) => {
                const showJoinBanner = resp.snapshot.val() as boolean
                return AppActions.storeJoinBannerState({ showJoinBanner })
            })
        )
    )

    fetchJoinBannerText$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.fetchJoinBannerText),
            switchMap(() => this.inCtx(() => object(ref(this.db, 'misc/joinBannerText')))),
            map((resp: QueryChange) => {
                const joinBannerText = resp.snapshot.val() as string
                return AppActions.storeJoinBannerText({ joinBannerText })
            })
        )
    )


    constructor(
        private actions$: Actions,
        private afs: Firestore,
        private db: Database,
        public dialog: MatDialog,
        private router: Router
    ) { }
}
